import { useEffect, useRef, useState } from 'react'
import rest from '../../rest'
import { Grid, Pagination, Message } from 'semantic-ui-react'
import CardPrioritario from '../CardPrioritario'
import Loading from '../loading'
import { tamanhoMaiorSigla } from '../../util/reportes'
import './styles.scss'
import { useStore } from '../../stores/useStoreProfile'
import { StorePrioritarioType } from '../../pages/FormPrioritario/types'

export default function CardsPrioritario({ setError }: { setError: (e: string | undefined) => void }) {
  const [cards, setCards] = useState<StorePrioritarioType[]>([])
  const [isLoaded, setIsLoaded] = useState(false)
  const [activePage, setActivePage] = useState(() => JSON.parse(localStorage.getItem("activePage") || '1'))

  const [totalPages, setTotalPages] = useState(1)
  const timeoutRef = useRef(setTimeout(() => 0))

  // Intervalo em segundos para cada paginada,
  // a atualização será a cada updateInterval * totalPages
  const updateInterval = 15

  const { store, modoApresentacao, api } = useStore()
  const { updatePermissions } = api
  const {
    change_itemtrabalho, add_relator, change_reporteandamento, change_resumo,
    pode_ser_relator, devolver_reporteandamento
  } = store.permissions

  function handleChangePage(actualPage: number) {
    localStorage.setItem("activePage", actualPage.toString())
    setActivePage(actualPage)
  }

  useEffect(() => {
    const getCards = async () => {
      return rest(`tema-prioritario/?page=${activePage}&page_size=6`).then(json => {
        setError(undefined)
        setIsLoaded(true)
        setCards(json.results)
        setTotalPages(json.num_pages)
        const timeout = !modoApresentacao || totalPages === 1 ? setTimeout(() => {
          getCards()
        }, updateInterval * totalPages * 1000)
          : null
        if (timeout) {
          timeoutRef.current = timeout
        }
      }).catch(err => {
        setIsLoaded(true)
        if (err.code === 'page_not_found' && activePage > 1) {
          handleChangePage(activePage - 1)
        } else {
          setError(err.detail)
        }
        timeoutRef.current = setTimeout(() => getCards(), updateInterval * 1000)
      })
    }

    getCards()
    return () => clearTimeout(timeoutRef.current)
  }, [activePage, modoApresentacao])  // eslint-disable-line

  useEffect(() => {
    const timer = modoApresentacao ? setTimeout(() => {
      setActivePage(activePage % totalPages + 1)
    }, updateInterval * 1000)
      : null
    return () => {
      timer && clearTimeout(timer)
    }
  }, [activePage, modoApresentacao, totalPages])

  useEffect(() => {
    const relator = Boolean(pode_ser_relator.filter((e: any) => e.area_relatora).length)
    if (relator) {
      updatePermissions()
    }
  }, [])  // eslint-disable-line

  if (!isLoaded) {
    return <Loading />
  }

  const canEditar = (item: StorePrioritarioType) => {
    const departamento = item.departamento?.toString()
    const editar = Boolean(change_itemtrabalho.find((e: any) => e.departamento === departamento))
    const atribuir = Boolean(add_relator.find((e: any) =>
      (e.departamento === departamento) &&
      item.areas_relatoras?.map(e => String(e.id)).includes(e.area_relatora)
    ))
    return editar || atribuir
  }
  const canRelatar = (item: StorePrioritarioType) => {
    const departamento = item.departamento?.toString()
    const id = item.id?.toString()

    const relatar = Boolean(
      change_reporteandamento.find((e: any) =>
        (e.itemtrabalho === id || e.departamento === departamento) &&
        item.areas_relatoras?.map(e => String(e.id)).includes(e.area_relatora)
      ) ||
      devolver_reporteandamento.find((e: any) =>
        e.departamento === departamento
      )
    )
    return relatar && item.alias_situacao === 'em_andamento'
  }
  const canResumir = (item: StorePrioritarioType) => {
    const departamento = item.departamento?.toString()
    const resumir = Boolean(change_resumo.find((e: any) => e.departamento === departamento))
    return resumir && item.alias_situacao === 'em_andamento'
  }

  return (
    <Grid doubling stackable columns={3} className={'cards-prioritario'}>
      {cards.length
        ? <>
          {cards.map(c =>
            <Grid.Column key={c.id}>
              <CardPrioritario
                id={c.id}
                key={c.id}
                title={c.titulo}
                situacao={c.ultimo_resumo?.situacao}
                deliberacao={c.ultimo_resumo?.deliberacao}
                novo={c.novo}
                notificacoes={c.notificacoes?.map(
                  c => ({ tipo: c.tipo_nom_alias, descricao: c.tipo_descricao })
                ) || []}
                atualizado_em={c.ultimo_resumo?.atualizado_em}
                reportes={c.ultimo_resumo?.reporteandamento_resumo || []}
                tamanhoMaiorSigla={tamanhoMaiorSigla(
                  cards.map(
                    c => ({ reportes: c.ultimo_resumo?.reporteandamento_resumo || [] })
                  )
                )}
                showEditar={canEditar(c)}
                showRelatar={canRelatar(c)}
                showResumir={canResumir(c)}
                setError={setError}
                backgroundColor={c.cor_fundo}
              />
            </Grid.Column>
          )}
          <Grid.Row centered>
            <Grid.Column textAlign='center' verticalAlign='bottom'>
              <Pagination
                activePage={activePage}
                totalPages={totalPages}
                onPageChange={(e, { activePage }) => handleChangePage(Number(activePage))}
                firstItem={null}
                lastItem={null}
              />
            </Grid.Column>
          </Grid.Row>
        </>
        : <Grid.Row centered>
          <Grid.Column width={8}>
            <Message compact icon='inbox' header='Não há dados' />
          </Grid.Column>
        </Grid.Row>
      }
    </Grid>
  )
}
